import { jsx as _jsx } from "nativewind/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import styled from 'styled-components/native';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { useToggleAccessibilityInfoRequest } from '@/hooks/useToggleAccessibilityInfoRequest';
import { useCheckAuth } from '@/utils/checkAuth';
import { PlaceCategoryDto } from '@/generated-sources/openapi';
/** 정보 요청하기 버튼이 노출되는 카테고리 */
const INFO_REQUEST_ELIGIBLE_CATEGORIES = [
    PlaceCategoryDto.Restaurant,
    PlaceCategoryDto.Cafe,
    PlaceCategoryDto.ConvenienceStore,
    PlaceCategoryDto.Hospital,
    PlaceCategoryDto.Pharmacy,
];
/** 정보 요청하기 기능 노출 차단 (프론트엔드 한정). 되살릴 땐 true 로 바꾼다. */
const INFO_REQUEST_ENABLED = false;
/** 정보 요청하기 버튼 노출 조건 */
export function isInfoRequestEligible(params) {
    if (!INFO_REQUEST_ENABLED)
        return false;
    if (params.hasPlaceAccessibility)
        return false;
    if (params.isConquestMode)
        return false;
    if (!params.address.startsWith('서울'))
        return false;
    if (!params.category ||
        !INFO_REQUEST_ELIGIBLE_CATEGORIES.includes(params.category)) {
        return false;
    }
    return true;
}
let highlightAnimationPlayed = false;
export function resetHighlightAnimation() {
    highlightAnimationPlayed = false;
}
export default function AccessibilityInfoRequestButton({ placeId, isRequested, animated: enableAnimation, listQueryKey, }) {
    const checkAuth = useCheckAuth();
    const toggleRequest = useToggleAccessibilityInfoRequest(listQueryKey);
    const scaleAnim = useRef(new Animated.Value(1)).current;
    const colorAnim = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        if (!enableAnimation || isRequested || highlightAnimationPlayed) {
            scaleAnim.setValue(1);
            colorAnim.setValue(0);
            return;
        }
        highlightAnimationPlayed = true;
        const singleCycle = Animated.sequence([
            // 1. Shrink
            Animated.timing(scaleAnim, {
                toValue: 0.88,
                duration: 180,
                useNativeDriver: false,
            }),
            // 2. Hold shrink
            Animated.delay(200),
            // 3. Grow bigger + color fill
            Animated.parallel([
                Animated.timing(scaleAnim, {
                    toValue: 1.1,
                    duration: 220,
                    useNativeDriver: false,
                }),
                Animated.timing(colorAnim, {
                    toValue: 1,
                    duration: 220,
                    useNativeDriver: false,
                }),
            ]),
            Animated.parallel([
                Animated.timing(scaleAnim, {
                    toValue: 1.08,
                    duration: 20,
                    useNativeDriver: false,
                }),
            ]),
            // 4. Hold big
            Animated.delay(800),
            // 5. Return to normal + color fade
            Animated.parallel([
                Animated.timing(scaleAnim, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: false,
                }),
                Animated.timing(colorAnim, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: false,
                }),
            ]),
        ]);
        const animation = Animated.sequence([
            Animated.delay(2000),
            singleCycle,
            Animated.delay(600),
            singleCycle,
        ]);
        animation.start();
        return () => animation.stop();
    }, [enableAnimation, isRequested, scaleAnim, colorAnim]);
    const handlePress = () => {
        checkAuth(() => {
            toggleRequest({
                currentIsRequested: isRequested,
                placeId,
            });
        });
    };
    if (enableAnimation && !isRequested) {
        const animatedBg = colorAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [color.brand40, color.white],
        });
        const animatedTextColor = colorAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [color.white, color.brand40],
        });
        const animatedBorderColor = colorAnim.interpolate({
            inputRange: [0, 1],
            outputRange: ['transparent', color.brand40],
        });
        return (_jsx(SccTouchableOpacity, { elementName: "accessibility_info_request_button", logParams: { placeId, isRequested }, activeOpacity: 0.6, onPress: handlePress, children: _jsx(Animated.View, { style: {
                    transform: [{ scale: scaleAnim }],
                }, children: _jsx(Animated.View, { style: {
                        backgroundColor: animatedBg,
                        padding: 5,
                        paddingHorizontal: 10,
                        borderRadius: 100,
                        borderWidth: 1,
                        borderColor: animatedBorderColor,
                    }, children: _jsx(Animated.Text, { style: {
                            fontSize: 12,
                            fontFamily: font.pretendardMedium,
                            color: animatedTextColor,
                        }, children: "\uC774 \uC7A5\uC18C\uC758 \uC811\uADFC\uC131 \uC815\uBCF4 \uC694\uCCAD\uD558\uAE30" }) }) }) }));
    }
    return (_jsx(ButtonContainer, { elementName: "accessibility_info_request_button", logParams: { placeId, isRequested }, activeOpacity: 0.6, onPress: handlePress, isRequested: isRequested, children: _jsx(RequestText, { isRequested: isRequested, children: isRequested
                ? '접근성 정보 요청한 장소'
                : '이 장소의 접근성 정보 요청하기' }) }));
}
const ButtonContainer = styled(SccTouchableOpacity) `
  padding: 5px 10px;
  border-radius: 100px;
  border-width: 1px;
  border-color: transparent;
  background-color: ${({ isRequested }) => isRequested ? color.gray20 : color.brand40};
`;
const RequestText = styled.Text `
  font-size: 12px;
  font-family: ${font.pretendardMedium};
  color: ${({ isRequested }) => (isRequested ? color.black : color.white)};
`;
